import { createContext, Dispatch, useContext } from "react";

/**
 * A helper to create a Context and Provider with no upfront default value, and
 * without having to check for undefined all the time.
 * Source: https://github.com/typescript-cheatsheets/react-typescript-cheatsheet#context
 */
export function createReactContext<A extends Record<string, unknown> | null>() {
  const ctx = createContext<A | undefined>(undefined);
  function useCtx() {
    const c = useContext(ctx);
    if (c === undefined) {
      throw new Error("useCtx must be inside a Provider with a value");
    }
    return c;
  }
  return [useCtx, ctx.Provider] as const; // 'as const' makes TypeScript infer a tuple
}

/**
 * A helper to create a Dispatch Context and Provider with no upfront default value, and
 * without having to check for undefined all the time.
 * Source: https://github.com/typescript-cheatsheets/react-typescript-cheatsheet#context
 */
export function createReactDispatchContext<
  A extends { type: string; payload?: Record<string, unknown> },
>() {
  const ctx = createContext<Dispatch<A> | undefined>(undefined);
  function useCtx() {
    const c = useContext(ctx);
    if (c === undefined) {
      throw new Error("useCtx must be inside a Provider with a value");
    }
    return c;
  }
  return [useCtx, ctx.Provider] as const; // 'as const' makes TypeScript infer a tuple
}
